import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Loader, Modal } from '@grandvisionhq/common';
import { Alert } from '@grandvisionhq/iris-ui';
import { useDispatch, useSelector, useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useDynamicModule } from '@grandvisionhq/utils';
import { useCheckoutSettings } from '../../../context/CheckoutSettings/context.js';
import '@grandvisionhq/carts-v2';
import '@grandvisionhq/graphql';
import '@grandvisionhq/payments';
import '@grandvisionhq/api-client';
import { resetStore, resetServiceStoreValidation } from '../../../StepShipping/actions.js';
import { getSelectedShippingMethod, shouldValidateServiceStore, getBillingAddress, getShippingDetails } from '../../../StepShipping/selectors.js';
import 'lodash/isEmpty';
import '../../../mappers/errors-mapping.js';
import '../../../utils/get-customer-details-payload.js';
import { isPickupStore } from '../../../utils/shipping-method.js';
import 'history';
import '../../../models/cart/cart-state.js';
import '../../../models/checkout/checkout-step-error-type.js';
import '../../../models/checkout-settings.js';
import '../../../models/step-shipping/shipping-methods.js';
import '../../../models/shared.js';
import '../../../utils/has-cart-errors.js';
import '@grandvisionhq/auth';
import { getServiceStore } from '../../../selectors.js';
import '@grandvisionhq/lib-utils-cart';
import '@grandvisionhq/svc-tealium/events';
import '../../../operations/mutations/set-customer-details.js';
import '../../../operations/mutations/set-service-store.js';
import '../../../operations/mutations/set-shipping-method.js';
import '../../../operations/mutations/set-shipping-state.js';
import '../../../operations/mutations/create-apple-pay-session.js';
import '../../../operations/mutations/recreate-ordered-checkout.js';
import { useSetServiceStore } from '../../../hooks/operations/use-set-service-store.js';
import '@grandvisionhq/frk-routing';
import '@grandvisionhq/fun-orders';
import '@grandvisionhq/analytics';
import '../../../operations/fragments/checkout-mutation-result.js';
import '../../../operations/fragments/cart-mutation-failures.js';
import '../../../operations/fragments/recreate-ordered-checkout-failure.js';
import '../../../operations/fragments/recreate-ordered-checkout-result.js';
import '../../../operations/fragments/paypal-payment.js';
import '../../../operations/fragments/saved-user-details.js';
import '../../../operations/queries/get-checkout-state.js';
import '../../../operations/queries/get-user-details.js';
import { useGetPreferredStore } from '../hooks/use-get-preferred-store.js';
import { useNearestStore } from '../hooks/use-nearest-store.js';
import { ActionButtonContainer } from './ActionButtonContainer.js';
import { StoreFinderItemContainer } from './StoreFinderItemContainer.js';
import { StoreSummaryContainer } from './StoreSummaryContainer.js';

const loadModule = function() {
    var _ref = swcHelpers.asyncToGenerator(function*() {
        const storesModule = yield import(/* webpackChunkName: 'grandvisionhq-stores-v2' */ '@grandvisionhq/stores-v2');
        return storesModule;
    });
    return function loadModule() {
        return _ref.apply(this, arguments);
    };
}();
const usePickupStoresModule = ()=>useDynamicModule(loadModule, '@grandvisionhq/stores-v2', [])
;
const ServiceStoreContainer = ({ className , handleServiceStoreLoading  })=>{
    const [showModal, setShowModal] = React.useState(false);
    const dispatch = useDispatch();
    const { serviceStore: serviceStoreConfig = {}  } = useCheckoutSettings();
    const selectedShippingMethod = useSelector(getSelectedShippingMethod);
    const serviceStore = useSelector(getServiceStore);
    const validateServiceStore = useSelector(shouldValidateServiceStore);
    const address = useSelector(getBillingAddress);
    const details = useSelector(getShippingDetails);
    const isPickupStoreSelected = isPickupStore(selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method);
    const serviceStoreIsInValid = validateServiceStore && !serviceStore;
    const serviceStoreIdRef = React.useRef(null);
    const { globalStoreId , isFetchingStoreId  } = useGetPreferredStore();
    const nearestStoreQuery = React.useMemo(()=>{
        if (details === null || details === void 0 ? void 0 : details.billingAddress.isValid) {
            const postalCode = (address === null || address === void 0 ? void 0 : address.postalCode) || '';
            const streetName = (address === null || address === void 0 ? void 0 : address.streetName) || '';
            const streetNumber = (address === null || address === void 0 ? void 0 : address.streetNumber) || '';
            const city = (address === null || address === void 0 ? void 0 : address.city) || '';
            return `${postalCode} ${streetName} ${streetNumber} ${city}`.replace(/\s{2,}/g, ' ').trim();
        }
        return '';
    }, [
        address,
        details
    ]);
    // Only attempt a service store preselect when the feature is enabled and there isn't already code selected.
    const [storeId1, isLoading] = useNearestStore(nearestStoreQuery, !!serviceStoreConfig.preselectFromShippingData && !serviceStore);
    const [serviceStoreIdState, setServiceStoreIdState] = React.useState(null);
    const { setServiceStore , isUpdating  } = useSetServiceStore({
        onError: ()=>{
            setServiceStoreIdState(null);
            dispatch(resetStore());
        }
    });
    React.useEffect(()=>{
        handleServiceStoreLoading === null || handleServiceStoreLoading === void 0 ? void 0 : handleServiceStoreLoading({
            isServiceStoreLoading: isUpdating
        });
    }, [
        isUpdating
    ]);
    React.useEffect(()=>{
        if (storeId1 && !serviceStoreIdState) {
            setServiceStoreIdState(storeId1);
        }
    }, [
        serviceStoreIdState,
        storeId1,
        isPickupStoreSelected
    ]);
    const [module, loading, error] = usePickupStoresModule();
    const { features  } = useCheckoutSettings();
    const featuresToggles = useFeatureToggles();
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            title: getLabel('checkout.serviceStore.title'),
            subtitle: getLabel('checkout.serviceStore.subtitle'),
            errorMessage: getLabel('checkout.serviceStore.error')
        })
    , [
        getLabel
    ]);
    React.useEffect(()=>{
        if (serviceStoreConfig.isOptional) {
            dispatch(resetServiceStoreValidation());
        }
    }, [
        dispatch,
        serviceStoreConfig.isOptional
    ]);
    const onStoreSelect = React.useCallback((store)=>{
        setShowModal(false);
        if (store === null || store === void 0 ? void 0 : store.globalStoreId) {
            setServiceStoreIdState(store.globalStoreId);
            setServiceStore(store.globalStoreId);
        }
    }, [
        setServiceStore
    ]);
    const onEdit = React.useCallback(()=>{
        setShowModal(true);
    }, []);
    const onCloseModal = React.useCallback(()=>{
        setShowModal(false);
    }, []);
    const resetServiceStore = React.useCallback(()=>{
        setServiceStoreIdState(null);
    }, []);
    const handleStoreChange = React.useCallback((storeId)=>{
        if (storeId !== serviceStoreIdState) {
            setServiceStoreIdState(storeId);
            setServiceStore(storeId);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        serviceStoreIdState
    ]);
    React.useEffect(()=>{
        if (!(serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.globalStoreId) && globalStoreId && globalStoreId !== serviceStoreIdRef.current) {
            // If the service store id is different from the one in the ref, call the handleStoreChange function
            handleStoreChange(globalStoreId);
            serviceStoreIdRef.current = globalStoreId;
        } else if ((serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.globalStoreId) && (serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.globalStoreId) !== serviceStoreIdRef.current) {
            handleStoreChange(serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.globalStoreId);
            serviceStoreIdRef.current = serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.globalStoreId;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        globalStoreId,
        serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.globalStoreId
    ]);
    if (!(features === null || features === void 0 ? void 0 : features.isServiceStoreEnabled) || isPickupStoreSelected) {
        return null;
    }
    if (loading || isFetchingStoreId) {
        return /*#__PURE__*/ React.createElement(Loader, null);
    }
    if (error || !module) {
        return /*#__PURE__*/ React.createElement(Alert, {
            variant: "error",
            notifyScreenReader: false,
            className: "alert-m-default"
        }, labels.errorMessage);
    }
    const { Components , Containers , StoreFinderProvider  } = module;
    const { StoreFinder , PickUpButtons , withPickUpButtonsConfig  } = Containers;
    const { StoreListTopContent  } = Components;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, serviceStoreIsInValid && /*#__PURE__*/ React.createElement(Alert, {
        variant: "error",
        notifyScreenReader: false,
        className: "alert-m-default"
    }, labels.errorMessage), serviceStoreIdState ? /*#__PURE__*/ React.createElement(StoreSummaryContainer, {
        className: className,
        globalStoreId: serviceStoreIdState,
        actions: {
            onEdit
        },
        resetServiceStore: resetServiceStore
    }) : /*#__PURE__*/ React.createElement(ActionButtonContainer, {
        onClick: onEdit,
        isLoading: isLoading,
        isValid: !serviceStoreIsInValid
    }), /*#__PURE__*/ React.createElement(Modal, {
        className: "service-store__modal",
        showIcon: true,
        iconClassName: "service-store__modal__close",
        isOpen: showModal,
        onModalClose: onCloseModal
    }, /*#__PURE__*/ React.createElement(StoreFinderProvider, {
        googleMapsId: "google-maps-service-store",
        hidePhone: !featuresToggles.isStorePickupPhoneEnabled,
        hideEmail: !featuresToggles.isStorePickupEmailEnabled
    }, /*#__PURE__*/ React.createElement(StoreFinder, {
        isInsideModal: true,
        listTopContent: /*#__PURE__*/ React.createElement(StoreListTopContent, {
            title: labels.title,
            description: labels.subtitle
        }),
        listItemComponent: (props)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, swcHelpers.extends({}, props, {
                variant: 'list-item',
                onClick: onStoreSelect
            }))
        ,
        infoWindowContentComponent: (props)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, swcHelpers.extends({}, props, {
                variant: 'map-item',
                onClick: onStoreSelect
            }))
        ,
        buttonsComponent: withPickUpButtonsConfig(PickUpButtons, {
            onStoreSelect
        })
    }))));
};

export { ServiceStoreContainer };
//# sourceMappingURL=ServiceStoreContainer.js.map
